import React, { memo, useState } from "react";
import { Box, Stack, Button, TextField, MenuItem, Card, IconButton, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import TruxSimpleNumberField from "src/components/inputs/trux-simple-number-field";
import Iconify from "src/components/iconify";
import { enqueueSnackbar } from "notistack";

// The core filter interface
export interface ReportFilter<T = any> {
  // Render the filter UI
  render: (props: FilterRenderProps<T>) => React.ReactNode;
  // Add this filter's data to the request object
  addToRequest: (request: Record<string, any>, data: T) => void;
  // Validate this filter's data
  validate: (request: Record<string, any>) => string | null;
  // Initial state for this filter
  initialState: T;
}

// Props passed to the filter's render function
export interface FilterRenderProps<T> {
  data: T;
  setData: (data: T) => void;
  disabled?: boolean;
}

// Main ReportRunner component
export interface ReportRunnerProps {
  filters: ReportFilter[];
  onValidate: (error: string) => void;
  onRun: (request: Record<string, any>) => void;
}

export function ReportRunner({ filters, onRun, onValidate }: ReportRunnerProps) {
  // Create state for each filter
  const [filterStates, setFilterStates] = useState<any[]>(
    filters.map(filter => filter.initialState)
  );

  const handleRun = () => {
    const request: Record<string, any> = {
      "ran": true
    };

    // Add each filter's data to the request
    filters.forEach((filter, index) => {
      filter.addToRequest(request, filterStates[index]);
    });

    // Validate all filters
    for (let i = 0; i < filters.length; i+=1) {
      const error = filters[i].validate(request);
      if (error) {
        // Assuming you have a notification system
        onValidate(error);
        return;
      }
    }

    // Send request to backend
    onRun(request);
  };

  return (
    <Card sx={{mb: 2}}>
      <Box sx={{p: 2}}>
        <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center">
          {filters.map((filter, index) => (
            <React.Fragment key={index}>
              {filter.render({
                data: filterStates[index],
                setData: (newData) => {
                  const newStates = [...filterStates];
                  newStates[index] = newData;
                  setFilterStates(newStates);
                }
              })}
            </React.Fragment>
          ))}

          <Button variant="contained" onClick={handleRun}>
            Run Report
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}

export function useReportRunnerConfigurator(filters: ReportFilter[]) {
  const [filterStates, setFilterStates] = useState<any[]>(
    filters.map(filter => filter.initialState)
  );

  return {
    run: (): [Record<string, any>?, string?] => {
      const request: Record<string, any> = {
        "ran": true
      };

      // Add each filter's data to the request
      filters.forEach((filter, index) => {
        filter.addToRequest(request, filterStates[index]);
      });

      // Validate all filters
      for (let i = 0; i < filters.length; i+=1) {
        const error = filters[i].validate(request);
        if (error) {
          // Assuming you have a notification system
          return [undefined, error];
        }
      }
      return [request, undefined];
    },
    reinit: () => {
      setFilterStates(filters.map(filter => filter.initialState))
    },
    render: (disabled: boolean) => <ReportRunnerConfigurator filters={filters} filterStates={filterStates} setFilterStates={setFilterStates} disabled={disabled} />
  }
}

export function ReportRunnerConfigurator({ filters, filterStates, setFilterStates, disabled }: {filters: ReportFilter[], filterStates: any[], setFilterStates: (a: any) => void, disabled: boolean}) {
  return (
    <Card sx={{mb: 2}}>
      <Typography variant="h6" component="h6" sx={{pt: 1, pl: 2}}>Report Configuration</Typography>
      <Box sx={{p: 2}}>
        <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center">
          {filters.map((filter, index) => (
            <React.Fragment key={index}>
              {filter.render({
                data: filterStates[index],
                setData: (newData) => {
                  const newStates = [...filterStates];
                  newStates[index] = newData;
                  setFilterStates(newStates);
                },
                disabled
              })}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
    </Card>
  );
}

export function useLandmarkFilter(): ReportFilter<{
  landmarkId: number | null,
}> {
  return {
    initialState: {
      landmarkId: 0,
    },
    render: ({ data, setData }) => (
      undefined
    ),
    addToRequest: (request, data) => {
      request.landmarkId = 0;
    },
    validate: () => null,
  };
}

export function useDaysDormantFilter(): ReportFilter<{
  daysDormant: number | null,
}> {
  const configs = [
    { id: 1, name: "1 Day" },
    { id: 2, name: "2 Days" },
    { id: 3, name: "3 Days" },
    { id: 4, name: "4 Days" },
    { id: 5, name: "5 Days" },
    { id: 6, name: "6 Days" },
    { id: 7, name: "7+ Days" },
  ];

  return {
    initialState: {
      daysDormant: 7,
    },
    render: ({ data, setData, disabled }) => (
      <TextField
        select
        label="Dormant For"
        value={data.daysDormant || 7}
        onChange={(e) => setData({ daysDormant: e.target.value ? Number(e.target.value) : 7 })}
        size="small"
        sx={{ minWidth: 180 }}
        disabled={disabled}
      >
        {configs.map((config) => (
          <MenuItem key={config.id} value={config.id}>
            {config.name}
          </MenuItem>
        ))}
      </TextField>
    ),
    addToRequest: (request, data) => {
      if (data.daysDormant) request.daysDormant = data.daysDormant;
    },
    validate: () => null,
  };
}
// Example filter hooks
export function useDateRangeFilter(): ReportFilter<{
  fromDate: string | null;
  toDate: string | null;
}> {
  return {
    initialState: {
      fromDate: null,
      toDate: null,
    },
    render: ({ data, setData, disabled }) => (
      <>
        <DatePicker
          label="From Date"
          value={!data.fromDate ? null : dayjs(data.fromDate)}
          onChange={(date: Dayjs | null) =>
            setData({ ...data, fromDate: date?.format("YYYY-MM-DD") || null })
          }
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small"
            },
          }}
          sx={{ maxWidth: { md: 180 } }}
          disabled={disabled}
        />
        <DatePicker
          label="To Date"
          value={!data.toDate ? null : dayjs(data.toDate)}
          onChange={(date: Dayjs | null) =>
            setData({ ...data, toDate: date?.format("YYYY-MM-DD") || null })
          }
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small"
            },
          }}
          sx={{ maxWidth: { md: 180 } }}
          disabled={disabled}
        />
      </>
    ),
    addToRequest: (request, data) => {
      if (data.fromDate) request.fromDate = data.fromDate;
      if (data.toDate) request.toDate = data.toDate;
    },
    validate: (request) => {
      if (!request.fromDate || !request.toDate) {
        return "Please select a date range";
      }
      return null;
    },
  };
}

export function useDateFilter(): ReportFilter<{
  date: string | null;
}> {
  return {
    initialState: {
      date: null,
    },
    render: ({ data, setData, disabled }) => (
      <DatePicker
        label="Date"
        value={!data.date ? null : dayjs(data.date)}
        onChange={(date: Dayjs | null) =>
          setData({ date: date?.format("YYYY-MM-DD") || null })
        }
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small"
          },
        }}
        sx={{ maxWidth: { md: 180 } }}
        disabled={disabled}
      />
    ),
    addToRequest: (request, data) => {
      if (data.date) request.date = data.date;
    },
    validate: (request) => {
      if (!request.date) {
        return "Please select a date";
      }
      return null;
    },
  };
}

export function useOrganizationFilter(): ReportFilter<{
  organizationId: string | null;
}> {
  // Mock organization data - replace with your actual data
  const organizations = [
    { id: "1", name: "Organization 1" },
    { id: "2", name: "Organization 2" },
    { id: "3", name: "Organization 3" },
  ];

  return {
    initialState: {
      organizationId: null,
    },
    render: ({ data, setData, disabled }) => (
      <TextField
        select
        label="Organization"
        value={data.organizationId || ""}
        onChange={(e) => setData({ organizationId: e.target.value || null })}
        size="small"
        sx={{ minWidth: 180 }}
        disabled={disabled}
      >
        <MenuItem value="">All Organizations</MenuItem>
        {organizations.map((org) => (
          <MenuItem key={org.id} value={org.id}>
            {org.name}
          </MenuItem>
        ))}
      </TextField>
    ),
    addToRequest: (request, data) => {
      if (data.organizationId) request.organizationId = data.organizationId;
    },
    validate: () => null, // Organization filter is optional
  };
}

type DownloadExcelButtonProps = {
  baseUrl: string, filters: ReportFilter[], currentRequest: React.MutableRefObject<any>
}
export const DownloadExcelButton = memo(({
  baseUrl, filters, currentRequest
}: DownloadExcelButtonProps) => <Button variant="outlined" startIcon={<Iconify icon="vscode-icons:file-type-excel2" />} onClick={() => {
    if (!currentRequest.current.ran) {
      enqueueSnackbar("Please run the report before downloading the excel", {variant:"error"});
      return
    }
    const error = filters.map(filter => filter.validate(currentRequest.current)).find(e => e);
    if (error) {
      enqueueSnackbar(error, {variant:"error"});
      return
    }

    let url = baseUrl;

    const keys = Object.keys(currentRequest.current);

    keys.forEach(key => {
      url += `${url.includes("?") ? "&" : "?"}${key}=${encodeURIComponent(currentRequest.current[key])}`
    })

    if (window.location.origin.includes("localhost")) {
      url = window.location.origin.replace("8035", "8080") + url;
    } else {
      url = window.location.origin.replace("app.", "api.") + url;
    }


    window.open(url, "_blank")
  }} >
    Download
  </Button>)
